import { type Day, type CutoffTime } from '@choco-admin/types';
import { Card, CardBody } from '@heroui/react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { DeliveryDaySettings } from '../../../../../../../components/DeliveryDaySettings';
import { ChocoCheckbox } from '../../../../../../../components/FormComponents';
import { useSupplierDeliverySetting } from '../../../../../hooks/useSupplierDeliverySetting';
import { useUpdateDeliverySetting } from '../../../../../hooks/useUpdateDeliverySetting';

import {
  CutoffTypeSelect,
  type CutoffType,
} from './components/CutoffTypeSelect';

interface DeliverySettingsFormData {
  hasCustomDeliverySettings: boolean;
  cutoffType: CutoffType;
  deliveryDays: Record<Day, boolean>;
  cutoffTimes: Record<Day, CutoffTime>;
  cutoffDays: Record<Day, string>;
}

interface DeliverySettingsSectionProps {
  chatId: string;
}

const DEFAULT_DELIVERY_DAYS: Record<Day, boolean> = {
  Monday: false,
  Tuesday: false,
  Wednesday: false,
  Thursday: false,
  Friday: false,
  Saturday: false,
  Sunday: false,
};

const DEFAULT_CUTOFF_TIMES: Record<Day, CutoffTime> = {
  Monday: '23:59:59',
  Tuesday: '23:59:59',
  Wednesday: '23:59:59',
  Thursday: '23:59:59',
  Friday: '23:59:59',
  Saturday: '23:59:59',
  Sunday: '23:59:59',
};

const DEFAULT_CUTOFF_DAYS: Record<Day, string> = {
  Monday: '1',
  Tuesday: '1',
  Wednesday: '1',
  Thursday: '1',
  Friday: '1',
  Saturday: '1',
  Sunday: '1',
};

export const DeliverySettingsSection: React.FC<
  DeliverySettingsSectionProps
> = ({ chatId }) => {
  const { watch, setValue } = useFormContext<DeliverySettingsFormData>();
  const initialized = useRef(false);
  const previousDeliveryDays = useRef<Record<Day, boolean>>(
    DEFAULT_DELIVERY_DAYS
  );

  // Get supplier delivery settings
  const { mappedSettings } = useSupplierDeliverySetting({ id: chatId });
  const { updateSettings } = useUpdateDeliverySetting();

  // Initialize form values once when mappedSettings are available
  useEffect(() => {
    if (!mappedSettings || initialized.current) return;

    setValue(
      'hasCustomDeliverySettings',
      mappedSettings.hasCustomDeliverySettings,
      { shouldDirty: false }
    );
    setValue('cutoffType', mappedSettings.cutoffType as CutoffType, {
      shouldDirty: false,
    });
    setValue('deliveryDays', mappedSettings.deliveryDays, {
      shouldDirty: false,
    });
    setValue('cutoffTimes', mappedSettings.cutoffTimes, { shouldDirty: false });
    setValue('cutoffDays', mappedSettings.cutoffDays, { shouldDirty: false });

    // Store initial delivery days for comparison
    previousDeliveryDays.current = mappedSettings.deliveryDays;
    initialized.current = true;
  }, [mappedSettings, setValue]);

  // Watch form values with defaults
  const hasCustomDeliverySettings = watch('hasCustomDeliverySettings') ?? false;
  const cutoffType = watch('cutoffType') ?? 'no_cutoff';
  const deliveryDays = watch('deliveryDays') ?? DEFAULT_DELIVERY_DAYS;
  const cutoffTimes = watch('cutoffTimes') ?? DEFAULT_CUTOFF_TIMES;
  const cutoffDays = watch('cutoffDays') ?? DEFAULT_CUTOFF_DAYS;

  // Update delivery settings when days change
  useEffect(() => {
    // Skip initial render and when no changes
    if (
      !initialized.current ||
      JSON.stringify(deliveryDays) ===
        JSON.stringify(previousDeliveryDays.current)
    ) {
      return;
    }

    // Update backend only if delivery days have changed
    updateSettings({
      chatId,
      deliveryDays,
      cutoffType,
      cutoffTimes,
      cutoffDays,
    });

    // Store new delivery days for next comparison
    previousDeliveryDays.current = deliveryDays;
  }, [
    chatId,
    deliveryDays,
    cutoffType,
    cutoffTimes,
    cutoffDays,
    updateSettings,
  ]);

  const handleToggleCustomSettings = useCallback(
    (isSelected: boolean) => {
      setValue('hasCustomDeliverySettings', isSelected, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleDayToggle = useCallback(
    (day: Day) => {
      setValue(`deliveryDays.${day}`, !deliveryDays[day], {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue, deliveryDays]
  );

  const handleCutoffTimeChange = useCallback(
    (day: Day, time: CutoffTime) => {
      setValue(`cutoffTimes.${day}`, time, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleCutoffDayChange = useCallback(
    (day: Day, cutoffDay: string) => {
      setValue(`cutoffDays.${day}`, cutoffDay, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  return (
    <Card>
      <CardBody className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Delivery Days & Cutoff Times
          </h3>
          <p className="text-default-600 text-sm">
            Set customer specific values that differ from the company settings.
            These settings apply for all products.
          </p>
        </div>

        <div className="border-b border-default-200 pb-6">
          <ChocoCheckbox
            isSelected={hasCustomDeliverySettings}
            onValueChange={handleToggleCustomSettings}
            size="sm"
            label="Enable customer specific delivery days & cutoff settings"
          />
        </div>

        {hasCustomDeliverySettings && (
          <div className="space-y-6">
            <CutoffTypeSelect />

            <div className="space-y-4">
              {(
                [
                  'Monday',
                  'Tuesday',
                  'Wednesday',
                  'Thursday',
                  'Friday',
                  'Saturday',
                  'Sunday',
                ] as Day[]
              ).map((day) => (
                <DeliveryDaySettings
                  key={day}
                  day={day}
                  isEnabled={deliveryDays[day] || false}
                  cutoffTime={cutoffTimes[day] || '23:59:59'}
                  cutoffDay={cutoffDays[day] || '1'}
                  onToggle={handleDayToggle}
                  onCutoffTimeChange={handleCutoffTimeChange}
                  onCutoffDayChange={handleCutoffDayChange}
                  errorMessage={
                    cutoffType === 'no_cutoff' ? undefined : undefined
                  }
                  cutoffType={cutoffType}
                />
              ))}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
